import React from 'react';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tag } from 'primereact/tag';
import { useGetLmsVideoByIdQuery } from '../../../../api';
import { useLanguage } from '../../../../contexts/LanguageContext';

// İzleme adresi kısa ömürlü olduğu için doldurulmadan önceki dakika sayısı — uzun bir video
// izlenirken adres dolmasın diye cömert tutuldu (bkz. docs/LmsVideoApi.md §4).
const WATCH_URL_EXPIRATION_MINUTES = 180;

/**
 * İzleme dialogu — Seviye 1 sıkılaştırma: indirme linki/butonu HİÇ YOK, sağ tık kapalı,
 * `controlsList="nodownload"`, adres her açılışta taze isteniyor (`keepUnusedDataFor: 0`,
 * bkz. api/endpoints/lmsVideo.js) ve oynatma sırasında hata alınırsa (`onError`) `refetch`
 * ile yenileniyor. Bu, S3 adresinin client'a hiç gösterilmediği tam bir proxy-stream değil —
 * bkz. İsmail ile konuşulan mimari karar (LmsVideoApi.md bu şekilde kurulu).
 */
const LmsVideoWatchDialog = ({ visible, onHide, videoId }) => {
    const { th } = useLanguage();

    const {
        data: video,
        isLoading,
        isFetching,
        isError,
        refetch
    } = useGetLmsVideoByIdQuery(
        { id: videoId, generateDownloadUrl: true, urlExpirationMinutes: WATCH_URL_EXPIRATION_MINUTES },
        { skip: !visible || !videoId }
    );

    const handleVideoError = () => {
        refetch();
    };

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={video?.tittle || th('lmsVideo.cardWatch')}
            modal
            style={{ width: '60rem', maxWidth: '95vw' }}
        >
            {(isLoading || isFetching) && (
                <div className="flex align-items-center justify-content-center" style={{ minHeight: '20rem' }}>
                    <ProgressSpinner style={{ width: '3rem', height: '3rem' }} strokeWidth="4" />
                </div>
            )}

            {!isLoading && !isFetching && isError && (
                <div className="p-4 text-center text-color-secondary">{th('lmsVideo.watchLoadError')}</div>
            )}

            {!isLoading && !isFetching && !isError && video && !video.downloadUrl && (
                <div className="p-4 text-center text-color-secondary">{th('lmsVideo.watchNoFile')}</div>
            )}

            {!isLoading && !isFetching && !isError && video?.downloadUrl && (
                <>
                    <video
                        key={video.downloadUrl}
                        src={video.downloadUrl}
                        controls
                        controlsList="nodownload noremoteplayback"
                        disablePictureInPicture
                        onContextMenu={(e) => e.preventDefault()}
                        onError={handleVideoError}
                        style={{ width: '100%', maxHeight: '32rem', backgroundColor: '#000000', borderRadius: '6px' }}
                    />

                    <div className="flex flex-column gap-2 mt-3">
                        <div className="flex align-items-center gap-2 flex-wrap">
                            {video.modelCode && <Tag value={video.modelCode} severity="secondary" />}
                            {video.feeTypeText && <Tag value={video.feeTypeText} severity="info" />}
                            <Tag value={th(video.isPublic ? 'lmsVideo.visibilityPublic' : 'lmsVideo.visibilityPrivate')} icon={video.isPublic ? 'pi pi-globe' : 'pi pi-lock'} severity={video.isPublic ? 'success' : 'warning'} />
                        </div>
                        {video.description && <span className="text-color-secondary text-sm">{video.description}</span>}
                        <span className="text-color-secondary text-xs mt-2">
                            <i className="pi pi-lock mr-1" />
                            {th('lmsVideo.watchViewOnlyNotice')}
                        </span>
                    </div>
                </>
            )}
        </Dialog>
    );
};

export default LmsVideoWatchDialog;
